import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface LearnSession {
  enrollmentId: string;
  courseId: string;
  brandId: string;
  companyId: string | null;
  studentName: string;
  studentEmail: string;
  courseName: string;
  expiresAt?: string;
  isValid: boolean;
}

interface LearnPortalContextType {
  session: LearnSession | null;
  isLoading: boolean;
  sendOTP: (email: string, companyEnrollmentId?: string, courseId?: string) => Promise<{ success: boolean; error?: string; retryAfter?: number }>;
  verifyOTP: (email: string, otp: string, courseId?: string) => Promise<{ success: boolean; error?: string; session?: LearnSession; attemptsRemaining?: number }>;
  logout: () => void;
  resendOTP: (email: string, courseId?: string) => Promise<{ success: boolean; error?: string; retryAfter?: number }>;
}

const LearnPortalContext = createContext<LearnPortalContextType | undefined>(undefined);

const STORAGE_KEY = 'learn-portal-session';

export const LearnPortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<LearnSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load session from localStorage on mount
  useEffect(() => {
    try {
      const storedSession = localStorage.getItem(STORAGE_KEY);
      if (storedSession) {
        const parsed = JSON.parse(storedSession) as LearnSession;
        
        // Check if session has expired
        if (parsed.expiresAt) {
          const expiryDate = new Date(parsed.expiresAt);
          if (expiryDate < new Date()) {
            console.log('[LearnPortal] Session expired, clearing');
            localStorage.removeItem(STORAGE_KEY);
            setSession(null);
            setIsLoading(false);
            return;
          }
        }
        
        setSession(parsed);
        console.log('[LearnPortal] Session loaded from storage');
      }
    } catch (error) {
      console.error('[LearnPortal] Error loading session:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
    setIsLoading(false);
  }, []);

  const sendOTP = useCallback(async (email: string, companyEnrollmentId?: string, courseId?: string) => {
    try {
      console.log('[LearnPortal] Sending OTP to:', email, 'Company:', companyEnrollmentId || 'none');
      
      const { data, error } = await supabase.functions.invoke('send-lms-otp', {
        body: { email, courseId, companyEnrollmentId },
      });

      if (error) {
        console.error('[LearnPortal] Error sending OTP:', error);
        return { success: false, error: 'Failed to send verification code' };
      }

      if (!data.success) {
        return { 
          success: false, 
          error: data.error || 'Failed to send verification code',
          retryAfter: data.retry_after 
        };
      }

      console.log('[LearnPortal] OTP sent successfully');
      return { success: true };
    } catch (error) {
      console.error('[LearnPortal] Unexpected error sending OTP:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }, []);

  const verifyOTP = useCallback(async (email: string, otp: string, courseId?: string) => {
    try {
      console.log('[LearnPortal] Verifying OTP for:', email);
      
      const { data, error } = await supabase.functions.invoke('verify-lms-otp', {
        body: { email, otp, courseId },
      });

      if (error) {
        console.error('[LearnPortal] Error verifying OTP:', error);
        return { success: false, error: 'Failed to verify code' };
      }

      if (!data.success) {
        return { 
          success: false, 
          error: data.error || 'Invalid verification code',
          attemptsRemaining: data.attempts_remaining 
        };
      }

      const newSession: LearnSession = {
        ...data.session,
        isValid: true,
      };

      setSession(newSession);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
      console.log('[LearnPortal] Session established');

      return { success: true, session: newSession };
    } catch (error) {
      console.error('[LearnPortal] Unexpected error verifying OTP:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }, []);

  const logout = useCallback(() => {
    console.log('[LearnPortal] Logging out');
    setSession(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const resendOTP = useCallback(async (email: string, courseId?: string) => {
    return sendOTP(email, courseId);
  }, [sendOTP]);

  const value: LearnPortalContextType = {
    session,
    isLoading,
    sendOTP,
    verifyOTP,
    logout,
    resendOTP,
  };

  return (
    <LearnPortalContext.Provider value={value}>
      {children}
    </LearnPortalContext.Provider>
  );
};

export const useLearnPortal = () => {
  const context = useContext(LearnPortalContext);
  if (context === undefined) {
    throw new Error('useLearnPortal must be used within a LearnPortalProvider');
  }
  return context;
};
